import { useEffect, useState, useRef } from 'react'
import Popover from '@/components/Popover'
import IconChevronDown from '@/assets/svgs/chevron-down.svg?react'
import PropTypes from 'prop-types'

/**
 * Ported from pitstop-ui/src/components/EformMultiSelectCheckbox
 *
 * Changes:
 *   - `immer` (produce) replaced with structuredClone for the single
 *     checkbox toggle mutation — same behaviour, zero new dep
 *   - No logic changes
 */

const EformMultiSelectCheckbox = ({ items, placeholder, onChangeFunc, disable }) => {
  const [selectedGroup, setSelectedGroup] = useState()
  const [filterVisible, setFilterVisible] = useState(false)
  const referenceRef = useRef(null)

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [])

  useEffect(() => {
    const index = items.findIndex(field => field.$selected === true)
    if (index < 0) {
      return setSelectedGroup()
    }
    return setSelectedGroup(items[index].groupId)
  }, [items])

  function handleDocumentClick(event) {
    if (!referenceRef.current) {
      return
    }
    if (referenceRef.current.contains(event.target)) {
      return
    }
    setFilterVisible(false)
  }

  if (!Array.isArray(items)) return null

  const count = items.filter(item => item.$selected === true).length
  const selectedOptions = items.filter(item => item.$selected === true)

  function changeListCheckboxes(event) {
    const index = items.findIndex(field => field.value == event.target.value)
    // immer (produce) replaced with structuredClone — same result
    const updatedItems = structuredClone(items)
    updatedItems[index].$selected = !updatedItems[index].$selected
    setSelectedGroup(updatedItems[index].groupId)
    onChangeFunc(updatedItems)
  }

  const list = items.map((field, index) => {
    return (
      <div className="flex" key={index}>
        <input
          type="checkbox"
          name={field.label || field.name}
          className="cursor-pointer align-middle accent-[var(--color-theme-50)]"
          value={field.value}
          id={field.value}
          checked={field.$selected}
          onChange={e => changeListCheckboxes(e)}
          disabled={selectedGroup && field.groupId !== selectedGroup}
        />
        <label
          htmlFor={field.value}
          className="inline-block mt-[5px] ml-[5px] font-normal cursor-pointer"
        >
          {field.label || field.name}
        </label>
      </div>
    )
  })

  return (
    <div className="[&>*]:z-[1] [&>*]:cursor-pointer">
      <Popover
        visible={filterVisible}
        options={{ modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] }}
      >
        <Popover.Trigger>
          <div
            className={`type-body-2 border rounded-[5px] flex items-center min-w-40.75 min-h-11.25 py-2.125 pr-2.75 pl-3.5 flex-wrap gap-1.25 ${disable ? 'text-grey-50 border-grey-90 bg-grey-98 [&_svg]:fill-grey-50' : 'border-grey-50'}`}
            onClick={() => {
              if (!disable) {
                setFilterVisible(!filterVisible)
              }
            }}
            data-testid="multi-checkbox-select-trigger"
          >
            {count > 0 &&
              disable &&
              selectedOptions?.map(option => (
                <div
                  key={`${option?.value}-${option?.label}`}
                  className="font-avenir type-body-2 border border-grey-50 rounded-[16px] py-0.75 px-1.5"
                >
                  {option?.label}
                </div>
              ))}
            {count > 0 && !disable && (
              <span className="font-avenir type-body-2">{count} Selected</span>
            )}
            {count === 0 && (
              <span
                className="font-avenir type-body-2"
                data-testid="multiCheckboxSelect__placeholder-text"
              >
                {disable ? 'No Item Selected' : placeholder}
              </span>
            )}
            {!disable && <IconChevronDown className="ml-auto" />}
          </div>
        </Popover.Trigger>

        <Popover.Content>
          <div
            className="border border-grey-70 rounded-[5px] bg-white overflow-y-scroll max-w-130 max-h-48.75 py-2.5 px-3.5"
            ref={referenceRef}
          >
            <div>
              <div className="flex flex-col gap-y-0.5">
                {list.length > 0 ? list : <span>No Data Available</span>}
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover>
    </div>
  )
}

EformMultiSelectCheckbox.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      $selected: PropTypes.bool,
      value: PropTypes.any,
      label: PropTypes.string,
      groupId: PropTypes.any,
    })
  ),
  placeholder: PropTypes.string,
  onChangeFunc: PropTypes.func,
  disable: PropTypes.bool,
}

export default EformMultiSelectCheckbox
